import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * THE LAST THING BETWEEN A RENDER BUG AND A BLANK PAGE.
 * ===========================================================================
 *
 * There was no error boundary anywhere in this app. React's default behaviour
 * when a render throws is to unmount the whole tree — so ONE unguarded lookup
 * anywhere below `main` produced a completely white page with no heading, no
 * navigation, no sign-out, and nothing said. A client with a case in flight
 * would have no way to tell a bug from their account being gone.
 *
 * The realistic trigger is not exotic. `CaseProgress` indexes two shared record
 * types by `caseRow.status`, and both are keyed by the client-facing statuses;
 * a status the server can return but the map does not carry reads `undefined`
 * and the render throws on the next property access. Any status added to
 * CASE_STATUSES without adding its copy does it, which is a one-line change in
 * another package.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO:
 *
 *  - It does not show the error. A stack or an exception message is a log
 *    artefact, not an account of what happened, and this codebase already
 *    refuses to print `internal_error` at a person for the same reason.
 *  - It does not offer "try again" as a re-render. The state that threw is
 *    still the state that would be re-rendered; a button that visibly does
 *    nothing is worse than no button. Reloading genuinely re-fetches.
 *  - It does not swallow the error silently — `componentDidCatch` still logs
 *    it, with the component stack, so the failure is diagnosable.
 *
 * MOUNTED INSIDE THE SHELL, NOT AROUND IT. The masthead and footer are outside,
 * so a crash leaves the person their identity, their sign-out and the link to
 * the data-handling page. Losing the page you were on is recoverable; losing
 * the way out of the application is not.
 */
type Props = { children: ReactNode };
type State = { crashed: boolean };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The one place this is allowed to be technical: it goes to the console, not
    // to the reader.
    console.error("Unhandled render error", error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.crashed) return this.props.children;

    return (
      <section className="auth-card">
        <h2>This screen did not load</h2>
        <p>
          Something in this page failed while it was being drawn. This is a fault on our side,
          not something you did, and nothing about your request has been changed or lost by it.
        </p>
        <p>
          Reloading the page is the thing most likely to work — it fetches everything again from
          the start.
        </p>
        <p>
          <button type="button" onClick={() => window.location.reload()}>
            Reload the page
          </button>
        </p>
      </section>
    );
  }
}
